import React, { useState, useEffect } from "react";
import { IconPlus, IconCalendar, IconClock, IconX, IconPencil, IconTrash, IconSearch, IconCheck, IconChevronLeft, IconChevronRight, IconUser } from "@tabler/icons-react";
import {
  createHrDepartment,
  deleteHrDepartment,
  updateHrDepartment,
  createHrWorkingFrom,
  deleteHrWorkingFrom,
  updateHrWorkingFrom,
  fetchHrDepartments,
  fetchHrWorkingFrom,
} from "../../../../apis/hrApi";
import toast from "react-hot-toast";

type EmployeeType = {
  _id: string;
  name: string;
  avatar?: string;
  designation: string;
  department: string;
};

type MarkAttendanceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  selectedDate?: string; // YYYY-MM-DD format
  employee?: EmployeeType;
  onSave?: (data: any) => void;
  employeesList?: any[];
  workingFromList?: any[];
  departmentsList?: any[];
  onRefreshMeta?: () => void;
  isSidebar?: boolean;
};

const InfoBlock = ({
  title,
  icon: Icon,
  children,
  className,
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-slate-200/50 shadow-sm p-4 transition-all duration-300 space-y-3 relative">
    <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/10 to-transparent pointer-events-none" />
    <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3 relative z-10">
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {title}
      </p>
    </div>
    <div className={`relative z-10 ${className || "grid grid-cols-1 sm:grid-cols-2 gap-4"}`}>
      {children}
    </div>
  </div>
);

const INPUT_CLASS = "w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold";
const INPUT_CLASS_WITH_ICON = "w-full rounded-xl py-[10px] pl-10 pr-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold";
const LABEL_CLASS = "text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block";

export default function MarkAttendanceModal({
  isOpen,
  onClose,
  mode,
  selectedDate = "2026-05-09",
  employee,
  onSave,
  employeesList = [],
  workingFromList = [],
  departmentsList = [],
  onRefreshMeta,
  isSidebar = false,
}: MarkAttendanceModalProps) {
  // Common states
  const [late, setLate] = useState("No");
  const [halfDay, setHalfDay] = useState("No");
  const [clockIn, setClockIn] = useState("");
  const [clockOut, setClockOut] = useState("");
  const [workingFrom, setWorkingFrom] = useState("");

  // Add Mode Specific States
  const [department, setDepartment] = useState("");
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [showEmpDropdown, setShowEmpDropdown] = useState(false);
  const [empSearch, setEmpSearch] = useState("");

  const [markBy, setMarkBy] = useState("Date");
  const [date, setDate] = useState("2026-05-22");
  
  // Multi-Date Picker Specific States
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showRangePicker, setShowRangePicker] = useState(false);
  const [currentMonthLeft, setCurrentMonthLeft] = useState(new Date().getMonth());
  const [currentYearLeft, setCurrentYearLeft] = useState(new Date().getFullYear());

  const [overwrite, setOverwrite] = useState(false);

  // Edit Mode Specific States
  const [clockInIp, setClockInIp] = useState("");
  const [clockOutIp, setClockOutIp] = useState("");
  const [attendanceBreak, setAttendanceBreak] = useState("");

  // Local list states for options management
  const [localDepts, setLocalDepts] = useState<any[]>([]);
  const [localWorkingFrom, setLocalWorkingFrom] = useState<any[]>([]);

  // Modals visibility
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [showWorkingFromModal, setShowWorkingFromModal] = useState(false);
  const [showBreakModal, setShowBreakModal] = useState(false);

  // Departments manager states
  const [deptName, setDeptName] = useState("");
  const [editingDeptId, setEditingDeptId] = useState<string | null>(null);
  const [deptIsSubmitting, setDeptIsSubmitting] = useState(false);

  // Working Froms manager states
  const [wfName, setWfName] = useState("");
  const [editingWfId, setEditingWfId] = useState<string | null>(null);
  const [wfIsSubmitting, setWfIsSubmitting] = useState(false);

  // Break Type states
  const [breakTitle, setBreakTitle] = useState("");
  const [breakAllowedDays, setBreakAllowedDays] = useState("1");
  const [breakTimeMinutes, setBreakTimeMinutes] = useState("30");
  const [breakStatus, setBreakStatus] = useState(true);
  const [breakOptions, setBreakOptions] = useState<string[]>(["Lunch", "Tea Break", "Medical Break"]);

  // Reload functions
  const reloadDepts = async () => {
    try {
      const res = await fetchHrDepartments(1, 100);
      if (res.success && res.data?.departments) {
        setLocalDepts(res.data.departments);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const reloadWorkingFroms = async () => {
    try {
      const res = await fetchHrWorkingFrom(1, 100);
      if (res.success) {
        let list: any[] = [];
        if (Array.isArray(res.data)) {
          list = res.data;
        } else if (res.data && typeof res.data === "object") {
          const possible = res.data.workingFroms || res.data.list || res.data.data || res.data.workingFrom;
          if (Array.isArray(possible)) {
            list = possible;
          } else {
            const found = Object.values(res.data).find(Array.isArray);
            if (found) list = found as any[];
          }
        }
        setLocalWorkingFrom(list);
      }
    } catch (e) {
      console.error(e);
    }
  };
  // Helper for generating calendar days grid
  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  const formatDatePickerDate = (year: number, month: number, day: number) => {
    return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const rightMonth = (currentMonthLeft + 1) % 12;
  const rightYear = currentMonthLeft === 11 ? currentYearLeft + 1 : currentYearLeft;

  const handlePrevMonth = () => {
    if (currentMonthLeft === 0) {
      setCurrentMonthLeft(11);
      setCurrentYearLeft(currentYearLeft - 1);
    } else {
      setCurrentMonthLeft(currentMonthLeft - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonthLeft === 11) {
      setCurrentMonthLeft(0);
      setCurrentYearLeft(currentYearLeft + 1);
    } else {
      setCurrentMonthLeft(currentMonthLeft + 1);
    }
  };

  const handleDayClick = (dateStr: string) => {
    setSelectedDates((prev) => {
      if (prev.includes(dateStr)) {
        return prev.filter((d) => d !== dateStr);
      } else {
        return [...prev, dateStr].sort();
      }
    });
  };

  const renderMonthCalendar = (m: number, y: number) => {
    const daysInMonth = getDaysInMonth(m, y);
    const firstDay = getFirstDayOfMonth(m, y);
    const blanks = Array(firstDay).fill(null);
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const allCells = [...blanks, ...days];

    return (
      <div className="w-[240px] space-y-3">
        {/* Month Header */}
        <div className="flex items-center justify-between font-bold text-sm text-[#212121] px-1">
          <span>{monthNames[m]} {y}</span>
        </div>

        {/* Days of Week Headers */}
        <div className="grid grid-cols-7 gap-1 text-center font-bold text-[10px] text-gray-400">
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
            <span key={i} className="w-7 h-7 flex items-center justify-center">{d}</span>
          ))}
        </div>

        {/* Days Grid */}
        <div className="grid grid-cols-7 gap-1">
          {allCells.map((day, idx) => {
            if (day === null) {
              return <div key={`blank-${idx}`} className="w-7 h-7" />;
            }
            const dateStr = formatDatePickerDate(y, m, day);
            const isSelected = selectedDates.includes(dateStr);

            let cellClass = "w-7 h-7 rounded-lg text-xs font-semibold flex items-center justify-center transition cursor-pointer select-none ";
            if (isSelected) {
              cellClass += "bg-[#FBD037] text-[#1a1a1a] font-bold ring-2 ring-[#FBD037]/40";
            } else {
              cellClass += "text-gray-700 hover:bg-[#FBD037]/20 hover:text-[#1a1a1a]";
            }

            return (
              <div
                key={`day-${day}`}
                onClick={() => handleDayClick(dateStr)}
                className={cellClass}
              >
                {day}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Populate data in edit mode & sync local states
  useEffect(() => {
    if (isOpen) {
      setLocalDepts(departmentsList);
      setLocalWorkingFrom(workingFromList);

      if (mode === "edit") {
        setClockIn("09:49");
        setClockOut("19:30");
        setClockInIp("192.168.7.93");
        setClockOutIp("192.168.7.93");
        setWorkingFrom("Office");
        setAttendanceBreak("Lunch");
        setLate("No");
        setHalfDay("No");
      } else {
        // Reset states for add mode
        setDepartment("");
        setSelectedEmployeeIds([]);
        setSelectedDates([]);
        setStartDate("");
        setEndDate("");
        setMarkBy("Date");
        setDate("2026-05-22");
        setClockIn("");
        setClockOut("");
        setWorkingFrom("");
        setLate("No");
        setHalfDay("No");
        setOverwrite(false);
      }
    }
  }, [isOpen, mode, employee, selectedDate, departmentsList, workingFromList]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload =
      mode === "add"
        ? {
            department,
            employeeIds: selectedEmployeeIds,
            employeeId: selectedEmployeeIds[0] || "",
            markBy,
            date,
            startDate: selectedDates[0] || startDate,
            endDate: selectedDates[selectedDates.length - 1] || endDate,
            selectedDates,
            late,
            halfDay,
            clockIn,
            clockOut,
            workingFrom,
            overwrite,
          }
        : {
            employeeId: employee?._id,
            date: selectedDate,
            clockIn,
            clockInIp,
            late,
            clockOut,
            clockOutIp,
            halfDay,
            workingFrom,
            attendanceBreak,
          };
    console.log("[MarkAttendance] workingFrom state:", workingFrom);
    console.log("[MarkAttendance] WORKING_FROM_OPTIONS:", WORKING_FROM_OPTIONS);
    console.log("[MarkAttendance] payload:", payload);
    if (onSave) onSave(payload);
    onClose();
  };

  // Dynamic choices from local states
  const DEPARTMENTS = localDepts.length > 0
    ? localDepts.map((d: any) => d.departmentName || d.name)
    : ["Human Resources", "IT & Support", "Sales", "Operations"];

  const EMPLOYEES = employeesList.length > 0
    ? employeesList
        .filter((emp: any) => {
          if (!department) return true;
          const empDept = emp.departmentName || emp.department?.departmentName || "";
          return empDept.toLowerCase() === department.toLowerCase();
        })
        .map((emp: any) => ({
          id: emp.id || emp._id,
          name: `${emp.firstName || ""} ${emp.lastName || ""}`.trim()
        }))
    : [
        { id: "1", name: "Sushil Rathore" },
        { id: "2", name: "Ruchi Rathore" },
        { id: "3", name: "Neha" },
      ];

  const WORKING_FROM_OPTIONS = localWorkingFrom.length > 0
    ? localWorkingFrom.map((w: any) => ({
        id: w.id || w._id || w.workingFromId || "",
        name: w.name || w.title || w.locationName || w.workingFromName || ""
      }))
    : [
        { id: "office", name: "Office" },
        { id: "home", name: "Home" },
        { id: "client-location", name: "Client Location" },
      ];

  const BREAK_OPTIONS = breakOptions;

  // CRUD handlers
  const handleDeptSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deptName.trim()) {
      toast.error("Department name is required");
      return;
    }
    setDeptIsSubmitting(true);
    try {
      if (editingDeptId) {
        const res = await updateHrDepartment(editingDeptId, { departmentName: deptName.trim() });
        if (res.success) {
          toast.success("Department updated successfully");
          setEditingDeptId(null);
          setDeptName("");
          await reloadDepts();
          if (onRefreshMeta) onRefreshMeta();
        } else {
          toast.error(res.message || "Failed to update department");
        }
      } else {
        const res = await createHrDepartment({ departmentName: deptName.trim() });
        if (res.success) {
          toast.success("Department created successfully");
          setDeptName("");
          await reloadDepts();
          if (onRefreshMeta) onRefreshMeta();
        } else {
          toast.error(res.message || "Failed to create department");
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setDeptIsSubmitting(false);
    }
  };

  const handleDeptDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this department?")) return;
    try {
      const res = await deleteHrDepartment(id);
      if (res.success) {
        toast.success("Department deleted successfully");
        await reloadDepts();
        if (onRefreshMeta) onRefreshMeta();
      } else {
        toast.error(res.message || "Failed to delete department");
      }
    } catch (err: any) {
      toast.error(err.message || "Something went wrong");
    }
  };

  const handleWfSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wfName.trim()) {
      toast.error("Working From name is required");
      return;
    }
    setWfIsSubmitting(true);
    try {
      if (editingWfId) {
        const res = await updateHrWorkingFrom(editingWfId, { name: wfName.trim() });
        if (res.success) {
          toast.success("Working From option updated successfully");
          setEditingWfId(null);
          setWfName("");
          await reloadWorkingFroms();
          if (onRefreshMeta) onRefreshMeta();
        } else {
          toast.error(res.message || "Failed to update working option");
        }
      } else {
        const res = await createHrWorkingFrom({ name: wfName.trim() });
        if (res.success) {
          toast.success("Working From option created successfully");
          setWfName("");
          await reloadWorkingFroms();
          if (onRefreshMeta) onRefreshMeta();
        } else {
          toast.error(res.message || "Failed to create working option");
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setWfIsSubmitting(false);
    }
  };

  const handleWfDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this option?")) return;
    try {
      const res = await deleteHrWorkingFrom(id);
      if (res.success) {
        toast.success("Working From option deleted successfully");
        await reloadWorkingFroms();
        if (onRefreshMeta) onRefreshMeta();
      } else {
        toast.error(res.message || "Failed to delete option");
      }
    } catch (err: any) {
      toast.error(err.message || "Something went wrong");
    }
  };
  const handleBreakSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!breakTitle.trim()) {
      toast.error("Break title is required");
      return;
    }
    const titleTrimmed = breakTitle.trim();
    if (breakOptions.includes(titleTrimmed)) {
      toast.error("Break type already exists");
      return;
    }
    setBreakOptions([...breakOptions, titleTrimmed]);
    setAttendanceBreak(titleTrimmed);
    toast.success("Break type created successfully");
    
    // reset form and close
    setBreakTitle("");
    setBreakAllowedDays("1");
    setBreakTimeMinutes("30");
    setBreakStatus(true);
    setShowBreakModal(false);
  };

  // Format date display for Edit mode header, e.g. "09-05-2026"
  const formattedHeaderDate = () => {
    if (!selectedDate) return "";
    const parts = selectedDate.split("-");
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return selectedDate;
  };

  if (!isOpen) return null;

  const modalWrapper = (children: React.ReactNode) => {
    if (isSidebar) {
      return (
        <div className="relative flex flex-col overflow-hidden bg-white w-full h-full z-10 animate-in fade-in slide-in-from-right duration-300">
          {children}
        </div>
      );
    }
    return (
      <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl border border-gray-100 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          {children}
        </div>
      </div>
    );
  };

  return (
    <>
      {modalWrapper(
        <form
          onSubmit={handleSubmit}
          className="w-full bg-white flex flex-col flex-1 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 bg-white/50 shrink-0">
            <div>
              <h2 className="text-xl font-semibold text-[#212121]">
                {mode === "add" ? "Mark Attendance" : `Mark Attendance (${formattedHeaderDate()})`}
              </h2>
              <p className="text-xs text-[#212121]/70">
                {mode === "add" ? "Mark employee attendance records" : "Edit attendance log details"}
              </p>
            </div>
            <div className="flex items-center gap-3 mt-1">
              <button
                type="submit"
                className="px-4 py-2 bg-[#FBD037] text-[#212121] text-sm font-semibold rounded-lg hover:bg-[#f5c71a] transition-colors border-none cursor-pointer shadow-sm"
              >
                Save
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors cursor-pointer bg-white text-slate-700 flex items-center justify-center"
              >
                <IconX size={18} />
              </button>
            </div>
          </div>

          {/* Form Body */}
          <div className={`p-6 overflow-y-auto space-y-6 scrollbar-clean flex-1 ${
            isSidebar ? "max-h-[calc(100vh-140px)]" : "max-h-[70vh]"
          }`}>
            {mode === "edit" && employee && (
              <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-white/90 to-white/60 border border-white/40 shadow-sm rounded-2xl">
                <div className="w-14 h-14 rounded-full p-[2px] bg-gradient-to-tr from-yellow-400 to-orange-300 shadow-sm">
                  <div className="w-full h-full rounded-full overflow-hidden bg-white flex items-center justify-center text-lg font-bold text-[#212121]">
                    {employee.avatar ? (
                      <img
                        src={employee.avatar}
                        alt={employee.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      employee.name.charAt(0)
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#212121]">
                    {employee.name}
                  </h3>
                  <p className="text-xs text-gray-500 font-semibold">
                    {employee.designation}{" "}
                    <span className="text-[10px] text-gray-400 font-medium">
                      · {employee.department}
                    </span>
                  </p>
                </div>
              </div>
            )}

            {mode === "add" ? (
              /* Add Mode Fields */
              <>
                {/* Group 1: Employee & Department */}
                <InfoBlock title="Employee Details" icon={IconUser}>
                  {/* Department */}
                  <div className="space-y-1.5">
                    <label className={LABEL_CLASS}>
                      Department <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2 items-center">
                      <select
                        required
                        value={department}
                        onChange={(e) => setDepartment(e.target.value)}
                        className="flex-1 rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
                      >
                        <option value="">Select Department...</option>
                        {DEPARTMENTS.map((d) => (
                           <option key={d} value={d}>
                             {d}
                           </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowDeptModal(true)}
                        className="p-2.5 rounded-xl bg-black text-white hover:bg-gray-800 transition cursor-pointer shrink-0"
                      >
                        <IconPlus size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Employee Multi-Select */}
                  <div className="space-y-1.5 relative">
                    <label className={LABEL_CLASS}>
                      Employee <span className="text-red-500">*</span>
                    </label>
                    <div
                      onClick={() => setShowEmpDropdown(!showEmpDropdown)}
                      className="w-full min-h-[38px] flex flex-wrap items-center gap-1.5 py-[6px] px-4 rounded-xl border border-[#D8D8D8] bg-white outline-none focus-within:border-[#FBD037] focus-within:ring-2 focus-within:ring-[#FBD037]/20 transition-all cursor-pointer select-none relative font-semibold"
                    >
                      {selectedEmployeeIds.length === 0 && (
                        <span className="text-sm text-gray-400">Select Employee</span>
                      )}
                      <div className="flex flex-wrap gap-1.5 items-center pr-14 max-w-full">
                        {selectedEmployeeIds.map((id) => {
                          const emp = EMPLOYEES.find((e) => e.id === id);
                          if (!emp) return null;
                          return (
                            <span
                              key={id}
                              className="inline-flex items-center gap-1 bg-gray-100 text-gray-800 text-[11px] font-bold px-2 py-0.5 rounded-lg border border-gray-200"
                            >
                              {emp.name}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedEmployeeIds(selectedEmployeeIds.filter((x) => x !== id));
                                }}
                                className="text-gray-400 hover:text-gray-600 font-semibold cursor-pointer border-none bg-transparent p-0 flex items-center justify-center"
                              >
                                <IconX size={10} />
                              </button>
                            </span>
                          );
                        })}
                      </div>

                      {/* Indicators */}
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 text-gray-400">
                        {selectedEmployeeIds.length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEmployeeIds([]);
                            }}
                            className="hover:text-gray-600 border-none bg-transparent cursor-pointer flex items-center justify-center p-0"
                          >
                            <IconX size={14} />
                          </button>
                        )}
                        <span className="text-gray-200">|</span>
                        <svg
                          className={`w-3.5 h-3.5 transition-transform ${showEmpDropdown ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>

                    {/* Dropdown Box */}
                    {showEmpDropdown && (
                      <>
                        {/* Transparent overlay click listener to close dropdown */}
                        <div
                          className="fixed inset-0 z-40 bg-transparent"
                          onClick={() => setShowEmpDropdown(false)}
                        />
                        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg p-2 max-h-[250px] overflow-y-auto space-y-1">
                          {/* Search Input */}
                          <div className="relative sticky top-0 bg-white pb-1 flex items-center gap-2">
                            <input
                              type="text"
                              placeholder="Search..."
                              value={empSearch}
                              onChange={(e) => setEmpSearch(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full px-3 py-1.5 pl-8 text-xs rounded-lg border border-gray-200 outline-none focus:ring-1 focus:ring-[#FBD037] focus:border-[#FBD037] text-gray-800 bg-white"
                            />
                            <IconSearch size={12} className="absolute left-2.5 top-1/2 -translate-y-[6px] text-gray-400 pointer-events-none" />
                          </div>

                          {/* List */}
                          {EMPLOYEES.filter((emp) =>
                            emp.name.toLowerCase().includes(empSearch.toLowerCase())
                          ).map((emp) => {
                            const isSelected = selectedEmployeeIds.includes(emp.id);
                            return (
                              <div
                                key={emp.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isSelected) {
                                    setSelectedEmployeeIds(selectedEmployeeIds.filter((id) => id !== emp.id));
                                  } else {
                                    setSelectedEmployeeIds([...selectedEmployeeIds, emp.id]);
                                  }
                                }}
                                className="flex items-center justify-between px-3 py-1.5 hover:bg-slate-50 rounded-lg cursor-pointer transition text-xs"
                              >
                                <div className="flex items-center gap-2.5">
                                  {/* Checkmark indicator */}
                                  <div className="w-4 h-4 flex items-center justify-center border border-gray-200 rounded bg-white">
                                    {isSelected && <IconCheck size={12} className="text-[#C9A100]" />}
                                  </div>

                                  {/* Avatar Initials */}
                                  <div className="w-7 h-7 rounded-full bg-[#E9F0F8] text-[#212121] flex items-center justify-center font-bold text-[10px]">
                                    {emp.name.charAt(0)}
                                  </div>

                                  <div>
                                    <p className="font-semibold text-gray-800">{emp.name}</p>
                                    <p className="text-[9px] text-gray-400">EMP#{emp.id.substring(0, 4)}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Late Radio */}
                  <div className="space-y-1.5">
                    <label className={LABEL_CLASS}>
                      Late
                    </label>
                    <div className="flex items-center gap-2.5 h-9">
                      <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700 select-none">
                        <input
                          type="radio"
                          name="lateAdd"
                          value="Yes"
                          checked={late === "Yes"}
                          onChange={() => setLate("Yes")}
                          className="h-4 w-4 appearance-none rounded-full border border-gray-300 checked:border-[#FBD037] checked:bg-[#FBD037] relative after:content-[''] after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:w-2 after:h-2 after:rounded-full after:bg-white after:opacity-0 checked:after:opacity-100 transition-all cursor-pointer"
                        />
                        Yes
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700 select-none">
                        <input
                          type="radio"
                          name="lateAdd"
                          value="No"
                          checked={late === "No"}
                          onChange={() => setLate("No")}
                          className="h-4 w-4 appearance-none rounded-full border border-gray-300 checked:border-[#FBD037] checked:bg-[#FBD037] relative after:content-[''] after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:w-2 after:h-2 after:rounded-full after:bg-white after:opacity-0 checked:after:opacity-100 transition-all cursor-pointer"
                        />
                        No
                      </label>
                    </div>
                  </div>
                </InfoBlock>

                {/* Group 2: Schedule & Method */}
                <InfoBlock title="Attendance Settings" icon={IconCalendar}>
                  {/* Mark Attendance By */}
                  <div className="space-y-1.5">
                    <label className={LABEL_CLASS}>
                      Mark Attendance By
                    </label>
                    <div className="flex items-center gap-2.5 h-9">
                      {["Date", "Multiple", "Month"].map((type) => (
                        <label
                          key={type}
                          className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700 select-none"
                        >
                          <input
                            type="radio"
                            name="markBy"
                            value={type}
                            checked={markBy === type}
                            onChange={() => setMarkBy(type)}
                            className="h-4 w-4 appearance-none rounded-full border border-gray-300 checked:border-[#FBD037] checked:bg-[#FBD037] relative after:content-[''] after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:w-2 after:h-2 after:rounded-full after:bg-white after:opacity-0 checked:after:opacity-100 transition-all cursor-pointer"
                          />
                          {type}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Select Date / Select Date Range */}
                  {markBy === "Multiple" ? (
                    <div className="space-y-1.5 relative col-span-2">
                      <label className="text-xs font-semibold text-gray-700 flex items-center gap-0.5">
                        Select Dates <span className="text-red-500">*</span>
                        {selectedDates.length > 0 && (
                          <span className="ml-auto text-[10px] font-bold text-[#C9A100] bg-[#FBD037]/20 px-1.5 py-0.5 rounded-md">
                            {selectedDates.length} selected
                          </span>
                        )}
                      </label>

                      {/* Selected dates tags */}
                      {selectedDates.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {selectedDates.map((d) => (
                            <span
                              key={d}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-[#FBD037]/20 text-[#1a1a1a] text-[10px] font-bold border border-[#FBD037]/40"
                            >
                              {d.split("-").reverse().join("-")}
                              <button
                                type="button"
                                onClick={() => setSelectedDates((prev) => prev.filter((x) => x !== d))}
                                className="text-gray-500 hover:text-red-500 cursor-pointer"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          <button
                            type="button"
                            onClick={() => setSelectedDates([])}
                            className="text-[10px] text-red-400 hover:text-red-600 font-semibold ml-1 cursor-pointer"
                          >
                            Clear all
                          </button>
                        </div>
                      )}

                      {/* Calendar trigger input */}
                      <div className="relative">
                        <input
                          type="text"
                          readOnly
                          placeholder="Click to select dates..."
                          value={selectedDates.length > 0 ? `${selectedDates.length} date(s) selected` : ""}
                          onClick={() => setShowRangePicker(!showRangePicker)}
                          className={INPUT_CLASS_WITH_ICON}
                        />
                        <IconCalendar
                          size={16}
                          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                        />
                      </div>

                      {showRangePicker && (
                        <>
                          {/* Overlay */}
                          <div
                            className="fixed inset-0 z-40 bg-transparent"
                            onClick={() => setShowRangePicker(false)}
                          />
                          <div className="absolute top-full left-0 mt-2 z-50 bg-white rounded-2xl border border-gray-200 shadow-2xl p-5 flex flex-col gap-4 animate-in fade-in duration-200 w-[550px]">
                            {/* Navigation Header */}
                            <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                              <button
                                type="button"
                                onClick={handlePrevMonth}
                                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 cursor-pointer bg-white"
                              >
                                <IconChevronLeft size={16} />
                              </button>
                              <span className="text-xs font-bold text-gray-500">
                                Select Dates · {selectedDates.length} selected
                              </span>
                              <button
                                type="button"
                                onClick={handleNextMonth}
                                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 cursor-pointer bg-white"
                              >
                                <IconChevronRight size={16} />
                              </button>
                            </div>

                            {/* Double month grids side-by-side */}
                            <div className="flex gap-6 justify-center">
                              {renderMonthCalendar(currentMonthLeft, currentYearLeft)}
                              {renderMonthCalendar(rightMonth, rightYear)}
                            </div>

                            {/* Done button */}
                            {selectedDates.length > 0 && (
                              <div className="pt-2 border-t border-gray-100 flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => setShowRangePicker(false)}
                                  className="px-4 py-1.5 rounded-xl bg-[#FBD037] text-[#1a1a1a] text-xs font-bold hover:bg-[#FACC15] transition cursor-pointer"
                                >
                                  Done ({selectedDates.length} selected)
                                </button>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <label className={LABEL_CLASS}>
                        Select Date <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="date"
                          required
                          value={date}
                          onChange={(e) => setDate(e.target.value)}
                          className={INPUT_CLASS_WITH_ICON}
                        />
                        <IconCalendar
                          size={16}
                          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                      </div>
                    </div>
                  )}

                  {/* Working From */}
                  <div className="space-y-1.5">
                    <label className={LABEL_CLASS}>
                      Working From <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2 items-center">
                      <select
                        required
                        value={workingFrom}
                        onChange={(e) => setWorkingFrom(e.target.value)}
                        className="flex-1 rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
                      >
                        <option value="">Select Working From...</option>
                        {WORKING_FROM_OPTIONS.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowWorkingFromModal(true)}
                        className="p-2.5 rounded-xl bg-black text-white hover:bg-gray-800 transition cursor-pointer shrink-0"
                      >
                        <IconPlus size={16} />
                      </button>
                    </div>
                  </div>
                </InfoBlock>

                {/* Group 3: Timing Details */}
                <InfoBlock title="Time & Attendance Status" icon={IconClock}>
                  {/* Clock In */}
                  <div className="space-y-1.5">
                    <label className={LABEL_CLASS}>
                      Clock In <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="time"
                        required
                        value={clockIn}
                        onChange={(e) => setClockIn(e.target.value)}
                        className={INPUT_CLASS_WITH_ICON}
                      />
                      <IconClock
                        size={16}
                        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                    </div>
                  </div>

                  {/* Clock Out */}
                  <div className="space-y-1.5">
                    <label className={LABEL_CLASS}>
                      Clock Out <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="time"
                        required
                        value={clockOut}
                        onChange={(e) => setClockOut(e.target.value)}
                        className={INPUT_CLASS_WITH_ICON}
                      />
                      <IconClock
                        size={16}
                        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                    </div>
                  </div>

                  {/* Half Day Radio */}
                  <div className="space-y-1.5">
                    <label className={LABEL_CLASS}>
                      Half Day
                    </label>
                    <div className="flex items-center gap-4 h-9">
                      <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700 select-none">
                        <input
                          type="radio"
                          name="halfDayAdd"
                          value="Yes"
                          checked={halfDay === "Yes"}
                          onChange={() => setHalfDay("Yes")}
                          className="h-4 w-4 appearance-none rounded-full border border-gray-300 checked:border-[#FBD037] checked:bg-[#FBD037] relative after:content-[''] after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:w-2 after:h-2 after:rounded-full after:bg-white after:opacity-0 checked:after:opacity-100 transition-all cursor-pointer"
                        />
                        Yes
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700 select-none">
                        <input
                          type="radio"
                          name="halfDayAdd"
                          value="No"
                          checked={halfDay === "No"}
                          onChange={() => setHalfDay("No")}
                          className="h-4 w-4 appearance-none rounded-full border border-gray-300 checked:border-[#FBD037] checked:bg-[#FBD037] relative after:content-[''] after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:w-2 after:h-2 after:rounded-full after:bg-white after:opacity-0 checked:after:opacity-100 transition-all cursor-pointer"
                        />
                        No
                      </label>
                    </div>
                  </div>
                </InfoBlock>

                {/* Attendance Overwrite */}
                <div className="bg-gradient-to-b from-white/90 to-white/60 border border-white/40 shadow-sm rounded-2xl p-5">
                  <label className="relative flex items-center cursor-pointer gap-3 select-none">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={overwrite}
                        onChange={(e) => setOverwrite(e.target.checked)}
                        className="peer h-5 w-5 cursor-pointer appearance-none rounded-lg border border-gray-300 checked:border-[#FBD037] checked:bg-[#FBD037] transition-all"
                      />
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3.5 w-3.5 absolute text-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200 pointer-events-none left-[3px] top-1/2 -translate-y-1/2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <span className="text-sm font-bold text-gray-700">
                      Attendance Overwrite
                    </span>
                  </label>
                </div>
              </>
            ) : (
              /* Edit Mode Fields - Unified 3-column Grid */
              <InfoBlock title="Attendance Details" icon={IconClock} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Clock In */}
                <div className="space-y-1.5">
                  <label className={LABEL_CLASS}>
                    Clock In <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      placeholder="Select Start Time"
                      value={clockIn}
                      onChange={(e) => setClockIn(e.target.value)}
                      className={INPUT_CLASS_WITH_ICON}
                    />
                    <IconClock
                      size={16}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                    />
                  </div>
                </div>

                {/* Clock In IP */}
                <div className="space-y-1.5">
                  <label className={LABEL_CLASS}>
                    Clock In IP
                  </label>
                  <input
                    type="text"
                    placeholder="Ex. 198.163.7.93"
                    value={clockInIp}
                    onChange={(e) => setClockInIp(e.target.value)}
                    className={INPUT_CLASS}
                  />
                </div>

                {/* Late Radio */}
                <div className="space-y-1.5">
                  <label className={LABEL_CLASS}>
                    Late
                  </label>
                  <div className="flex items-center gap-4 h-9">
                    <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-700 select-none">
                      <input
                        type="radio"
                        name="lateEdit"
                        value="Yes"
                        checked={late === "Yes"}
                        onChange={() => setLate("Yes")}
                        className="h-4 w-4 appearance-none rounded-full border border-gray-300 checked:border-[#FBD037] checked:bg-[#FBD037] relative after:content-[''] after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-white after:opacity-0 checked:after:opacity-100 transition-all cursor-pointer"
                      />
                      Yes
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-700 select-none">
                      <input
                        type="radio"
                        name="lateEdit"
                        value="No"
                        checked={late === "No"}
                        onChange={() => setLate("No")}
                        className="h-4 w-4 appearance-none rounded-full border border-gray-300 checked:border-[#FBD037] checked:bg-[#FBD037] relative after:content-[''] after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-white after:opacity-0 checked:after:opacity-100 transition-all cursor-pointer"
                      />
                      No
                    </label>
                  </div>
                </div>

                {/* Clock Out */}
                <div className="space-y-1.5">
                  <label className={LABEL_CLASS}>
                    Clock Out <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      placeholder="Select Start Time"
                      value={clockOut}
                      onChange={(e) => setClockOut(e.target.value)}
                      className={INPUT_CLASS_WITH_ICON}
                    />
                    <IconClock
                      size={16}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                    />
                  </div>
                </div>

                {/* Clock Out IP */}
                <div className="space-y-1.5">
                  <label className={LABEL_CLASS}>
                    Clock Out IP
                  </label>
                  <input
                    type="text"
                    placeholder="Ex. 198.163.7.93"
                    value={clockOutIp}
                    onChange={(e) => setClockOutIp(e.target.value)}
                    className={INPUT_CLASS}
                  />
                </div>

                {/* Half Day Radio */}
                <div className="space-y-1.5">
                  <label className={LABEL_CLASS}>
                    Half Day
                  </label>
                  <div className="flex items-center gap-4 h-9">
                    <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-700 select-none">
                      <input
                        type="radio"
                        name="halfDayEdit"
                        value="Yes"
                        checked={halfDay === "Yes"}
                        onChange={() => setHalfDay("Yes")}
                        className="h-4 w-4 appearance-none rounded-full border border-gray-300 checked:border-[#FBD037] checked:bg-[#FBD037] relative after:content-[''] after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-white after:opacity-0 checked:after:opacity-100 transition-all cursor-pointer"
                      />
                      Yes
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-700 select-none">
                      <input
                        type="radio"
                        name="halfDayEdit"
                        value="No"
                        checked={halfDay === "No"}
                        onChange={() => setHalfDay("No")}
                        className="h-4 w-4 appearance-none rounded-full border border-gray-300 checked:border-[#FBD037] checked:bg-[#FBD037] relative after:content-[''] after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-white after:opacity-0 checked:after:opacity-100 transition-all cursor-pointer"
                      />
                      No
                    </label>
                  </div>
                </div>

                {/* Working From */}
                <div className="space-y-1.5">
                  <label className={LABEL_CLASS}>
                    Working From <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2 items-center">
                    <select
                      required
                      value={workingFrom}
                      onChange={(e) => setWorkingFrom(e.target.value)}
                      className="flex-1 rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
                    >
                      <option value="">Select Working From...</option>
                      {WORKING_FROM_OPTIONS.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowWorkingFromModal(true)}
                      className="p-2.5 rounded-xl bg-black text-white hover:bg-gray-800 transition cursor-pointer shrink-0 border-none animate-none"
                    >
                      <IconPlus size={16} />
                    </button>
                  </div>
                </div>

                {/* Attendance Break */}
                <div className="space-y-1.5">
                  <label className={LABEL_CLASS}>
                    Attendance Break <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2 items-center">
                    <select
                      required
                      value={attendanceBreak}
                      onChange={(e) => setAttendanceBreak(e.target.value)}
                      className="flex-1 rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
                    >
                      <option value="">Select Attendance Break...</option>
                      {BREAK_OPTIONS.map((b) => (
                        <option key={b} value={b}>
                          {b}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowBreakModal(true)}
                      className="p-2.5 rounded-xl bg-black text-white hover:bg-gray-800 transition cursor-pointer shrink-0 border-none animate-none"
                    >
                      <IconPlus size={16} />
                    </button>
                  </div>
                </div>
              </InfoBlock>
            )}
          </div>
        </form>
      )}

    {/* Departments Manager Modal */}
    {showDeptModal && (
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-100 p-6 space-y-4 animate-in fade-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="text-base font-bold text-[#212121]">Departments</h3>
            <button
              type="button"
              onClick={() => {
                setShowDeptModal(false);
                setEditingDeptId(null);
                setDeptName("");
              }}
              className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-slate-500 cursor-pointer bg-white"
            >
              <IconX size={16} />
            </button>
          </div>

          {/* Input Form */}
          <form onSubmit={handleDeptSubmit} className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-700">
                Name <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  required
                  placeholder="Enter Department name"
                  value={deptName}
                  onChange={(e) => setDeptName(e.target.value)}
                  className="flex-1 px-3 py-2 text-xs rounded-xl border border-gray-200 outline-none focus:ring-1 focus:ring-[#FBD037] focus:border-[#FBD037] text-gray-800 bg-white"
                />
                <button
                  type="submit"
                  disabled={deptIsSubmitting}
                  className="px-4 py-2 bg-black hover:bg-gray-800 text-white font-bold text-xs rounded-xl transition cursor-pointer flex items-center gap-1.5 border-none"
                >
                  {deptIsSubmitting ? "Saving..." : editingDeptId ? "Update" : "Submit"}
                </button>
              </div>
            </div>
          </form>

          {/* List Table */}
          <div className="max-h-[220px] overflow-y-auto border border-gray-100 rounded-xl">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-50 text-gray-500 font-bold uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="px-4 py-2 w-12">Sr</th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2 text-center w-24">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {localDepts.map((d, index) => (
                  <tr key={d.id || d._id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-2 text-gray-400 font-semibold">{index + 1}</td>
                    <td className="px-4 py-2 font-medium text-gray-800">{d.departmentName || d.name}</td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingDeptId(d.id || d._id);
                            setDeptName(d.departmentName || d.name || "");
                          }}
                          className="p-1 rounded border border-gray-200 hover:bg-gray-50 text-slate-500 transition cursor-pointer bg-white"
                        >
                          <IconPencil size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeptDelete(d.id || d._id)}
                          className="p-1 rounded border border-red-100 hover:bg-red-50 text-red-500 transition cursor-pointer bg-white"
                        >
                          <IconTrash size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {localDepts.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center py-4 text-gray-400">
                      No departments found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )}

    {/* Working Froms Manager Modal */}
    {showWorkingFromModal && (
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-100 p-6 space-y-4 animate-in fade-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="text-base font-bold text-[#212121]">Working Froms</h3>
            <button
              type="button"
              onClick={() => {
                setShowWorkingFromModal(false);
                setEditingWfId(null);
                setWfName("");
              }}
              className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-slate-500 cursor-pointer bg-white"
            >
              <IconX size={16} />
            </button>
          </div>

          {/* Input Form */}
          <form onSubmit={handleWfSubmit} className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-700">
                Name <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  required
                  placeholder="Enter Working From name"
                  value={wfName}
                  onChange={(e) => setWfName(e.target.value)}
                  className="flex-1 px-3 py-2 text-xs rounded-xl border border-gray-200 outline-none focus:ring-1 focus:ring-[#FBD037] focus:border-[#FBD037] text-gray-800 bg-white"
                />
                <button
                  type="submit"
                  disabled={wfIsSubmitting}
                  className="px-4 py-2 bg-black hover:bg-gray-800 text-white font-bold text-xs rounded-xl transition cursor-pointer flex items-center gap-1.5 border-none"
                >
                  {wfIsSubmitting ? "Saving..." : editingWfId ? "Update" : "Submit"}
                </button>
              </div>
            </div>
          </form>

          {/* List Table */}
          <div className="max-h-[220px] overflow-y-auto border border-gray-100 rounded-xl">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-50 text-gray-500 font-bold uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="px-4 py-2 w-12">Sr</th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2 text-center w-24">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {localWorkingFrom.map((w, index) => {
                  const name = w.name || w.title || w.locationName || w.workingFromName || "";
                  const id = w.id || w._id || w.workingFromId || "";
                  return (
                    <tr key={id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-2 text-gray-400 font-semibold">{index + 1}</td>
                      <td className="px-4 py-2 font-medium text-gray-800">{name}</td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingWfId(id);
                              setWfName(name);
                            }}
                            className="p-1 rounded border border-gray-200 hover:bg-gray-50 text-slate-500 transition cursor-pointer bg-white"
                          >
                            <IconPencil size={12} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleWfDelete(id)}
                            className="p-1 rounded border border-red-100 hover:bg-red-50 text-red-500 transition cursor-pointer bg-white"
                          >
                            <IconTrash size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {localWorkingFrom.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center py-4 text-gray-400">
                      No locations found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )}
    {/* Add Break Type Modal */}
    {showBreakModal && (
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-100 p-6 space-y-4 animate-in fade-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="text-base font-bold text-[#212121]">Add Break Type</h3>
            <button
              type="button"
              onClick={() => {
                setShowBreakModal(false);
                setBreakTitle("");
                setBreakAllowedDays("1");
                setBreakTimeMinutes("30");
                setBreakStatus(true);
              }}
              className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-slate-500 cursor-pointer bg-white"
            >
              <IconX size={16} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleBreakSubmit} className="space-y-4">
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-700 flex items-center gap-0.5">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ex. Lunch Break"
                  value={breakTitle}
                  onChange={(e) => setBreakTitle(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl border border-gray-200 outline-none focus:ring-1 focus:ring-[#FBD037] focus:border-[#FBD037] text-gray-800 bg-white"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-700 flex items-center gap-0.5">
                  Allowed In Days <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  required
                  placeholder="1"
                  value={breakAllowedDays}
                  onChange={(e) => setBreakAllowedDays(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl border border-gray-200 outline-none focus:ring-1 focus:ring-[#FBD037] focus:border-[#FBD037] text-gray-800 bg-white"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-700 flex items-center gap-0.5">
                  Break Time (minutes) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  required
                  placeholder="30"
                  value={breakTimeMinutes}
                  onChange={(e) => setBreakTimeMinutes(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl border border-gray-200 outline-none focus:ring-1 focus:ring-[#FBD037] focus:border-[#FBD037] text-gray-800 bg-white"
                />
              </div>

              <div className="flex items-center justify-between pt-1">
                <span className="text-xs font-semibold text-gray-700">Status</span>
                <button
                  type="button"
                  onClick={() => setBreakStatus(!breakStatus)}
                  className={`w-9 h-5 rounded-full transition-colors relative cursor-pointer outline-none border-none ${
                    breakStatus ? "bg-[#212121]" : "bg-gray-200"
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all ${
                      breakStatus ? "translate-x-4" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setShowBreakModal(false);
                  setBreakTitle("");
                  setBreakAllowedDays("1");
                  setBreakTimeMinutes("30");
                  setBreakStatus(true);
                }}
                className="px-5 py-2 border border-gray-300 rounded-xl text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-black hover:bg-gray-800 text-white rounded-xl text-xs font-bold transition cursor-pointer shadow-sm border-none"
              >
                Create Break Type
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </>
  );
}
